// Gestiona el ciclo de vida de la instalación de la PWA en un único lugar.
// El evento `beforeinstallprompt` solo se dispara una vez por carga, así que
// lo capturamos a nivel de módulo y lo exponemos por subscripción para que
// tanto el banner inferior (InstallPrompt) como el botón de Ajustes puedan
// dispararlo sin "robárselo" el uno al otro.

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type Platform = 'ios' | 'android-chromium' | 'desktop' | 'other';
export type Browser = 'firefox' | 'safari-desktop' | 'edge' | 'chromium' | 'other';

export function getBrowser(): Browser {
  const ua = navigator.userAgent;
  if (/firefox|fxios/i.test(ua)) return 'firefox';
  // Edge se identifica con "Edg/" (la versión nueva basada en Chromium).
  // Hay que comprobarlo antes que chromium porque su UA también contiene "Chrome".
  if (/edg\//i.test(ua)) return 'edge';
  // Safari de escritorio: contiene "Safari" pero no "Chrome"/"Chromium"/"Edg"
  if (/^((?!chrome|chromium|crios|edg|android).)*safari/i.test(ua) && !/iphone|ipad|ipod/i.test(ua)) {
    return 'safari-desktop';
  }
  if (/chrome|chromium|crios|edg/i.test(ua)) return 'chromium';
  return 'other';
}

export interface InstallState {
  standalone: boolean;
  platform: Platform;
  canPromptInstall: boolean;
  installed: boolean;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<(s: InstallState) => void>();

function isIOSUA() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isAndroidUA() {
  return /android/i.test(navigator.userAgent);
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function getPlatform(): Platform {
  if (isIOSUA()) return 'ios';
  if (isAndroidUA()) return 'android-chromium';
  if (/macintosh|windows|linux/i.test(navigator.userAgent)) return 'desktop';
  return 'other';
}

export function getInstallState(): InstallState {
  return {
    standalone: isStandalone(),
    platform: getPlatform(),
    canPromptInstall: deferred !== null,
    installed,
  };
}

function emit() {
  const state = getInstallState();
  listeners.forEach((l) => l(state));
}

export function subscribeInstallState(cb: (s: InstallState) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

// El evento puede consumirse una sola vez; quien lo dispare debe llamar a
// `tryInstall`, que lo limpia tras `userChoice` para que un segundo intento
// no falle silenciosamente.
export async function tryInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  const evt = deferred;
  deferred = null;
  emit();
  try {
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    return outcome;
  } catch {
    return 'dismissed';
  }
}

let initialized = false;
export function initInstallApp() {
  if (initialized) return;
  initialized = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    emit();
  });
}

// ── Alias con los nombres pedidos (envuelven la API existente) ────────────────
export function canPromptInstall(): boolean { return deferred !== null; }
export async function promptInstall(): Promise<boolean> {
  return (await tryInstall()) === 'accepted';
}
export function wasInstalled(): boolean { return installed; }
export function onInstallChange(fn: (s: InstallState) => void): () => void {
  return subscribeInstallState(fn);
}

// ── Detección fina de plataforma/navegador (para instrucciones manuales) ──────

export type DetectPlatform = 'ios' | 'android' | 'desktop';
export type DetectBrowser =
  | 'edge' | 'samsung' | 'chrome-ios' | 'firefox' | 'chrome' | 'safari' | 'other';

// iOS real: iPhone/iPad/iPod y también iPadOS 13+, que se hace pasar por
// Macintosh y solo se distingue por el soporte táctil.
function isIOS(): boolean {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  const nav = navigator as Navigator & { platform?: string; maxTouchPoints?: number };
  if (nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1) return true;
  return false;
}

export function detect(): { platform: DetectPlatform; browser: DetectBrowser } {
  const ua = navigator.userAgent;
  const ios = isIOS();
  const platform: DetectPlatform = ios ? 'ios' : /android/i.test(ua) ? 'android' : 'desktop';

  // Orden importante: Edge/Samsung/CriOS/Firefox llevan "Chrome"/"Safari" en su
  // UA, así que se comprueban ANTES que chrome/safari.
  let browser: DetectBrowser;
  if (/edg(a|ios)?\//i.test(ua)) browser = 'edge';           // Edg/ , EdgA/ (Android), EdgiOS/
  else if (/samsungbrowser/i.test(ua)) browser = 'samsung';
  else if (/crios/i.test(ua)) browser = 'chrome-ios';        // Chrome en iOS
  else if (/fxios|firefox/i.test(ua)) browser = 'firefox';   // Firefox (incl. iOS)
  else if (/chrome|chromium/i.test(ua) && !ios) browser = 'chrome';
  else if (/safari/i.test(ua)) browser = 'safari';           // Safari (iOS o macOS)
  else browser = 'other';

  return { platform, browser };
}

// ── Pasos de instalación manual por plataforma/navegador ─────────────────────

export type InstallIcon = 'share' | 'add' | 'menu' | 'apps' | 'install' | 'safari';
export interface InstallStep { text: string; icon: InstallIcon | null; }
export interface InstallGuide { title: string; note?: string; steps: InstallStep[]; }

// Devuelve la guía ya traducida. Recibe la `t` de i18n para no acoplar el
// servicio a react-i18next. Las claves viven en components.install.guide.*.
export function manualSteps(
  d: { platform: DetectPlatform; browser: DetectBrowser },
  t: (key: string) => string,
): InstallGuide {
  const g = (k: string) => t(`components.install.guide.${k}`);
  const { platform, browser } = d;

  if (platform === 'ios') {
    return {
      title: g('ios.title'),
      // En iOS SOLO Safari puede instalar (Añadir a pantalla de inicio). Si el
      // navegador no es Safari, avisamos de abrirlo en Safari.
      note: browser !== 'safari' ? g('ios.noteSafari') : undefined,
      steps: [
        { text: g('ios.s1'), icon: 'share' },
        { text: g('ios.s2'), icon: 'add' },
        { text: g('ios.s3'), icon: null },
      ],
    };
  }

  if (platform === 'android') {
    if (browser === 'samsung') {
      return { title: g('samsung.title'), steps: [
        { text: g('samsung.s1'), icon: 'menu' },
        { text: g('samsung.s2'), icon: 'add' },
        { text: g('samsung.s3'), icon: null },
      ] };
    }
    if (browser === 'firefox') {
      return { title: g('androidFirefox.title'), steps: [
        { text: g('androidFirefox.s1'), icon: 'menu' },
        { text: g('androidFirefox.s2'), icon: 'install' },
        { text: g('androidFirefox.s3'), icon: null },
      ] };
    }
    // Chrome, Edge Android y resto de Chromium.
    return { title: g('androidChrome.title'), steps: [
      { text: g('androidChrome.s1'), icon: 'menu' },
      { text: g('androidChrome.s2'), icon: 'install' },
      { text: g('androidChrome.s3'), icon: null },
    ] };
  }

  // Escritorio.
  if (browser === 'edge') {
    return { title: g('edge.title'), note: g('edge.note'), steps: [
      { text: g('edge.s1'), icon: 'menu' },
      { text: g('edge.s2'), icon: 'apps' },
      { text: g('edge.s3'), icon: 'install' },
    ] };
  }
  if (browser === 'chrome') {
    return { title: g('chromeDesktop.title'), steps: [
      { text: g('chromeDesktop.s1'), icon: 'install' },
      { text: g('chromeDesktop.s2'), icon: 'menu' },
      { text: g('chromeDesktop.s3'), icon: null },
    ] };
  }
  if (browser === 'safari') {
    return { title: g('macSafari.title'), steps: [
      { text: g('macSafari.s1'), icon: 'menu' },
      { text: g('macSafari.s2'), icon: 'install' },
      { text: g('macSafari.s3'), icon: null },
    ] };
  }
  if (browser === 'firefox') {
    return { title: g('firefoxDesktop.title'), note: g('firefoxDesktop.note'), steps: [] };
  }
  return { title: g('generic.title'), note: g('generic.note'), steps: [] };
}
